import React, { useState, useEffect, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface HeroSliderProps {
  slides: Array<{ image: string; title: string; link: string }>;
  onSlideClick: (link: string) => void;
}

const HeroSlider = memo(({ slides, onSlideClick }: HeroSliderProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTabActive, setIsTabActive] = useState(true);

  // Pause slider when tab is inactive
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabActive(!document.hidden);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Auto-slide only when tab is active
  useEffect(() => {
    if (slides.length === 0 || !isTabActive) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [slides.length, isTabActive]);

  if (slides.length === 0) return null;

  // Whitelisted domains for security
  const isAllowedDomain = (url: string) => {
    const allowedDomains = ['youtube.com', 'instagram.com', 'lastzone.in'];
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      return allowedDomains.some(allowed => domain.includes(allowed));
    } catch {
      return false;
    }
  };

  const handleClick = () => {
    const link = slides[currentSlide].link;
    if (!link) return;
    
    if (link.startsWith('http')) {
      if (isAllowedDomain(link)) {
        window.open(link, '_blank');
      } else {
        console.warn('External link blocked for security');
      }
    } else {
      onSlideClick(link);
    }
  };

  return (
    <div className="relative rounded-2xl overflow-hidden h-64 md:h-80 mb-8">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          onClick={handleClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }} // Reduced from 0.5
          className="absolute inset-0 cursor-pointer"
        >
          <img
            src={slides[currentSlide].image}
            alt={slides[currentSlide].title}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="text-white font-rajdhani font-bold text-xl md:text-2xl mb-2">
                {slides[currentSlide].title}
              </h3>
              <button className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg text-white text-sm font-rajdhani font-bold">
                Check Now →
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
      
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              idx === currentSlide ? 'w-6 bg-purple-500' : 'w-1.5 bg-white/30'
            }`}
          />
        ))}
      </div>
    </div>
  );
});

HeroSlider.displayName = 'HeroSlider';
export default HeroSlider;